<script lang="ts">
	import { clickOutside } from '@repo/commons/utils'
	import { Icon, OverlayScrollbar } from '@repo/commons/components'
	import { fade, scale } from 'svelte/transition'
	import { portal } from 'svelte-portal'
	import type { Snippet } from 'svelte'
	import type { DropdownItem } from '@repo/commons/models'
	import '../../styles/dropdown.css'

	interface Props {
		children: Snippet<
			[{ isOpen: boolean; toggle: () => void; open: () => void; close: () => void }]
		>
		items?: DropdownItem[]
		placement?:
			| 'bottom-start'
			| 'bottom-end'
			| 'top-start'
			| 'top-end'
			| 'right-start'
			| 'right-end'
			| 'left-start'
			| 'left-end'
		offset?: number
		crossAxisOffset?: number
		minWidth?: number
		maxHeight?: number
		onSelect?: ((item: DropdownItem) => void) | null
		onOpen?: (() => void) | null
		onClose?: (() => void) | null
		id?: string
		disabled?: boolean
		theme?: 'light' | 'dark'
		size?: 'md' | 'sm'
		useFixedPosition?: boolean
		portalTarget?: string | HTMLElement | null
	}

	let {
		children,
		items = [],
		placement = 'bottom-start',
		offset = 8,
		minWidth = 200,
		maxHeight = 300,
		onSelect = null,
		onOpen = null,
		onClose = null,
		id = `dropdown-${Math.random().toString(36).substring(2, 9)}`,
		disabled = false,
		theme = 'light',
		size = 'md',
		portalTarget = 'body',
		useFixedPosition = false,
		crossAxisOffset = 0
	}: Props = $props()

	let triggerWrapper: HTMLElement | null = $state(null)
	let childrenWrapper: HTMLElement | null = $state(null)
	let isOpen = $state(false)

	let dropdownPosition = $state({ top: 0, left: 0, width: 0 })

	function calculateDropdownPosition() {
		if (!triggerWrapper) return

		const rect = triggerWrapper.getBoundingClientRect()
		const width = Math.max(rect.width, minWidth)

		const scrollOffsetY = 0
		const scrollOffsetX = 0

		const baseTop = {
			bottom: rect.bottom + offset,
			top: rect.top - offset,
			start: rect.top + crossAxisOffset,
			end: rect.bottom - crossAxisOffset
		}

		const baseLeft = {
			start: rect.left + crossAxisOffset,
			end: rect.right - width - crossAxisOffset,
			right: rect.right + offset,
			left: rect.left - width - offset
		}

		const [primary, secondary] = placement.split('-')

		// Calculate dropdown height (single pass)
		const dropdownHeight = Math.min(
			maxHeight,
			items.reduce((total, { separator }) => total + (separator ? 8 : 48), 0)
		)

		let top = 0
		let left = 0

		if (primary === 'bottom' || primary === 'top') {
			top = baseTop[primary as 'bottom' | 'top'] + scrollOffsetY
			left = baseLeft[secondary as 'start' | 'end'] + scrollOffsetX
		} else {
			left = baseLeft[primary as 'right' | 'left'] + scrollOffsetX
			top = baseTop[secondary as 'start' | 'end'] + scrollOffsetY
			if (secondary === 'end') {
				top -= dropdownHeight
			}
		}

		const viewportTop = scrollOffsetY
		const viewportBottom = window.innerHeight + scrollOffsetY

		if (primary === 'bottom' && top + dropdownHeight > viewportBottom) {
			top = rect.top - offset - dropdownHeight + scrollOffsetY
		} else if (primary === 'top' && top - dropdownHeight < viewportTop) {
			top = rect.bottom + offset + scrollOffsetY
		} else if (primary === 'right' || primary === 'left') {
			if (top + dropdownHeight > viewportBottom) {
				top = viewportBottom - dropdownHeight - 8
			}
			if (top < viewportTop) {
				top = viewportTop + 8
			}
		}

		// Horizontal bounds
		if (left + width > window.innerWidth) {
			left = window.innerWidth - width - 16
		}

		if (left < 0) {
			left = 16
		}

		dropdownPosition = { top, left, width }
	}

	function toggleDropdown() {
		if (disabled) return

		if (!isOpen) {
			calculateDropdownPosition()
			onOpen?.()
		} else {
			onClose?.()
		}
		isOpen = !isOpen
	}

	function openDropdown() {
		if (disabled || isOpen) return
		calculateDropdownPosition()
		isOpen = true
		onOpen?.()
	}

	function closeDropdown() {
		if (!isOpen) return
		isOpen = false
		onClose?.()
	}

	function selectItem(item: DropdownItem) {
		if (item.disabled || item.separator) return

		onSelect?.(item)
		closeDropdown()
	}

	function handleClickOutside() {
		closeDropdown()
	}

	function handleKeydown(e: KeyboardEvent) {
		if (disabled) return

		switch (e.key) {
			case 'Escape':
				e.preventDefault()
				closeDropdown()
				break
			case 'Enter':
			case ' ':
				e.preventDefault()
				toggleDropdown()
				break
		}
	}

	function handleResize() {
		if (isOpen) {
			calculateDropdownPosition()
		}
	}

	function handleScroll() {
		if (isOpen) {
			calculateDropdownPosition()
		}
	}

	// Expose methods for parent component
	export function open() {
		openDropdown()
	}

	export function close() {
		closeDropdown()
	}

	export function toggle() {
		toggleDropdown()
	}

	$effect(() => {
		if (isOpen) {
			window.addEventListener('resize', handleResize)
			window.addEventListener('scroll', handleScroll, true)

			return () => {
				window.removeEventListener('resize', handleResize)
				window.removeEventListener('scroll', handleScroll, true)
			}
		}
	})
</script>

<svelte:window on:resize={handleResize} on:scroll={handleScroll} />

<div class="dropdown-wrapper" {id} bind:this={childrenWrapper}>
	<div
		class="dropdown-trigger"
		bind:this={triggerWrapper}
		role="button"
		tabindex={disabled ? -1 : 0}
		aria-haspopup="true"
		aria-expanded={isOpen}
		aria-controls="{id}-menu"
		class:disabled
		onclick={toggleDropdown}
		onkeydown={handleKeydown}
	>
		{@render children?.({
			isOpen,
			toggle: toggleDropdown,
			open: openDropdown,
			close: closeDropdown
		})}
	</div>
</div>

{#if isOpen && items.length}
	<div
		use:portal={portalTarget || document.body}
		use:clickOutside={handleClickOutside}
		class="dropdown-menu {theme} {size}"
		id="{id}-menu"
		role="menu"
		style="position: fixed; top: {dropdownPosition.top}px; left: {dropdownPosition.left}px; width: {dropdownPosition.width}px; z-index: 9999; max-height: {maxHeight}px;"
		in:scale={{ duration: 200, start: 0.95 }}
		out:fade={{ duration: 150 }}
	>
		<OverlayScrollbar class="dropdown-scrollable-wrapper">
			<div class="dropdown-items">
				{#each items as item (item.id)}
					{#if item.separator}
						<div class="dropdown-separator" role="separator"></div>
					{:else}
						<div
							class="dropdown-item"
							class:disabled={item.disabled}
							class:active={item.active}
							role="menuitem"
							tabindex={item.disabled ? -1 : 0}
							onclick={() => selectItem(item)}
							onkeydown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									selectItem(item)
								}
							}}
						>
							{#if item.icon}
								<div class="item-icon">
									<Icon
										name={item.icon}
										size={size === 'sm' ? 'sm' : 'md'}
										class={`text-[${theme === 'dark' ? '#FFFFFF99' : '#002C4D80'}]`}
									/>
								</div>
							{/if}
							<span class="item-label">{item.label}</span>
						</div>
					{/if}
				{/each}
			</div>
		</OverlayScrollbar>
	</div>
{/if}
